import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

type GymServiceSnapshot = {
  gimnasio_id: string;
  suscripcion_id: string | null;
  saas_plan_id: string | null;
  plan_codigo: string | null;
  plan_nombre: string | null;
  estado: string | null;
  periodo_inicio: string | null;
  periodo_fin: string | null;
  grace_until: string | null;
  operable: boolean;
  motivo: string;
};

type GymPago = {
  id: string;
  monto: number;
  moneda: string;
  metodo: string;
  estado: string;
  referencia_externa: string | null;
  fecha_pago: string | null;
  created_at: string;
};

const MOTIVO_LABEL: Record<string, string> = {
  operativo: 'Operativo',
  operativo_exento_owner: 'Operativo (owner exento)',
  gimnasio_inactivo: 'Gimnasio inactivo',
  bloqueo_manual_owner: 'Bloqueo manual por owner',
  sin_suscripcion: 'Sin suscripcion activa',
  suscripcion_suspendida: 'Suscripcion suspendida',
  suscripcion_vencida: 'Suscripcion vencida',
};

export default function PagarServicioPage() {
  const { profile } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<GymServiceSnapshot | null>(null);
  const [pagos, setPagos] = useState<GymPago[]>([]);
  const [planPrice, setPlanPrice] = useState<{ monto: number; moneda: string } | null>(null);

  const gimnasioId = profile?.gimnasioId || null;

  const warning = useMemo(() => {
    if (!snapshot) return null;
    if (snapshot.operable) return null;
    return MOTIVO_LABEL[snapshot.motivo] || snapshot.motivo || 'Servicio suspendido';
  }, [snapshot]);

  const isOwnerExempt = snapshot?.motivo === 'operativo_exento_owner';

  const paymentFeedback = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const payment = (params.get('payment') || '').toLowerCase();
    if (!payment) return null;
    if (payment === 'ok') {
      return { cls: 'bg-green-50 border-green-200 text-green-700', msg: 'Pago confirmado correctamente.' };
    }
    if (payment === 'rejected') {
      return { cls: 'bg-red-50 border-red-200 text-red-700', msg: 'El pago fue rechazado por Transbank.' };
    }
    if (payment === 'canceled') {
      return { cls: 'bg-amber-50 border-amber-200 text-amber-700', msg: 'Pago cancelado.' };
    }
    return null;
  }, [location.search]);

  useEffect(() => {
    if (!gimnasioId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [snapRes, pagosRes] = await Promise.all([
          supabase.rpc('gym_service_snapshot', { target_gym_id: gimnasioId }),
          supabase
            .from('gimnasio_pagos')
            .select('id,monto,moneda,metodo,estado,referencia_externa,fecha_pago,created_at')
            .eq('gimnasio_id', gimnasioId)
            .order('created_at', { ascending: false })
            .limit(12),
        ]);

        if (snapRes.error) throw snapRes.error;
        if (pagosRes.error) throw pagosRes.error;

        const row = Array.isArray(snapRes.data) && snapRes.data.length > 0 ? snapRes.data[0] : null;
        setSnapshot((row as GymServiceSnapshot | null) || null);
        setPagos((pagosRes.data as GymPago[]) || []);

        if (row?.saas_plan_id) {
          const { data: planRow } = await supabase
            .from('saas_planes')
            .select('precio,moneda')
            .eq('id', row.saas_plan_id)
            .maybeSingle();
          if (planRow) {
            setPlanPrice({
              monto: Number((planRow as any).precio || 0),
              moneda: String((planRow as any).moneda || 'CLP'),
            });
          } else {
            setPlanPrice(null);
          }
        } else {
          setPlanPrice(null);
        }
      } catch (err: any) {
        setError(err?.message || 'No se pudo cargar el estado del servicio');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [gimnasioId]);

  const submitTransbankForm = (url: string, token: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.style.display = 'none';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'token_ws';
    input.value = token;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  };

  const handleCheckout = async () => {
    try {
      setError(null);
      setProcessingCheckout(true);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
      }

      const { data, error: fnError } = await supabase.functions.invoke('saas-payments-create-checkout', {
        body: { idempotency_key: crypto.randomUUID() },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (fnError) {
        let detailed = fnError.message || 'No se pudo iniciar el checkout';
        const response = (fnError as any)?.context;
        if (response && typeof response.json === 'function') {
          const body = await response.json().catch(() => null);
          if (body?.error) {
            detailed = body.error;
            if (body?.details) {
              detailed += `: ${typeof body.details === 'string' ? body.details : JSON.stringify(body.details)}`;
            }
          } else if (body?.message) {
            detailed = body.message;
          }
        }
        throw new Error(detailed);
      }

      const providerUrl = String((data as any)?.url || '');
      const providerToken = String((data as any)?.token || '');
      if (!providerUrl || !providerToken) {
        throw new Error('Respuesta invalida al iniciar el pago');
      }

      submitTransbankForm(providerUrl, providerToken);
    } catch (err: any) {
      setError(err?.message || 'No se pudo iniciar el pago');
      setProcessingCheckout(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Pagar Servicio</h1>
          <p className="text-gray-600 mt-1">Estado de tu suscripcion SaaS y pagos del sistema.</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {paymentFeedback && (
          <div className={`mb-6 border px-4 py-3 rounded-lg ${paymentFeedback.cls}`}>
            {paymentFeedback.msg}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-gray-500">Cargando estado de servicio...</div>
        ) : !snapshot ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
            No hay informacion de suscripcion para este gimnasio. Contacta al owner.
          </div>
        ) : (
          <>
            {warning && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                <i className="ri-lock-2-line"></i>
                Servicio suspendido: {warning}. Regulariza el pago para habilitar el sistema.
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <Card title="Plan SaaS" value={snapshot.plan_nombre || 'Sin plan'} subtitle={snapshot.plan_codigo || '-'} />
              <Card
                title="Estado"
                value={isOwnerExempt ? 'active' : (snapshot.estado || '-')}
                subtitle={
                  isOwnerExempt
                    ? 'Operativo (owner exento)'
                    : snapshot.operable
                    ? 'Operativo'
                    : (MOTIVO_LABEL[snapshot.motivo] || snapshot.motivo)
                }
                valueClassName={snapshot.operable ? 'text-green-600' : 'text-red-600'}
              />
              <Card
                title="Periodo actual"
                value={isOwnerExempt ? 'Sin vencimiento' : (snapshot.periodo_fin ? formatDate(snapshot.periodo_fin) : '-')}
                subtitle={
                  isOwnerExempt
                    ? 'Gimnasio principal exento de cobro SaaS'
                    : snapshot.periodo_inicio
                    ? `Inicio: ${formatDate(snapshot.periodo_inicio)}`
                    : 'Sin fechas'
                }
              />
              <Card
                title="Monto a pagar"
                value={planPrice ? `${planPrice.moneda} $${Number(planPrice.monto).toLocaleString('es-CL')}` : '-'}
                subtitle={snapshot.plan_nombre || 'Sin plan'}
              />
            </div>

            {isOwnerExempt ? (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-teal-900 mb-2">Servicio owner exento</h2>
                <p className="text-sm text-teal-700">
                  Este gimnasio es el principal del owner. No requiere pagos SaaS y no tiene fecha de vencimiento.
                </p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Renovar servicio SaaS</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Este pago usa Transbank con credenciales del owner de plataforma y es independiente de "Metodos de pago" del gimnasio.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleCheckout}
                    disabled={processingCheckout}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <i className="ri-bank-card-line"></i>
                    {processingCheckout ? 'Redirigiendo...' : 'Pagar con Transbank'}
                  </button>
                  <a
                    href="mailto:soporte@gymadmin.cl?subject=Renovar%20servicio%20SaaS"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    <i className="ri-mail-send-line"></i>
                    Contactar soporte
                  </a>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Historial de pagos SaaS</h2>
              {pagos.length === 0 ? (
                <p className="text-sm text-gray-500">Aun no hay pagos registrados.</p>
              ) : (
                <div className="space-y-3">
                  {pagos.map((p) => (
                    <div key={p.id} className="border border-gray-100 rounded-lg px-4 py-3 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {p.moneda} ${Number(p.monto).toLocaleString('es-CL')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {p.metodo} - {p.referencia_externa || 'sin referencia'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDateTime(p.fecha_pago || p.created_at)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          p.estado === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : p.estado === 'pending'
                            ? 'bg-amber-100 text-amber-700'
                            : p.estado === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {p.estado}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Card({
  title,
  value,
  subtitle,
  valueClassName = 'text-gray-900',
}: {
  title: string;
  value: string;
  subtitle: string;
  valueClassName?: string;
}) {
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <p className="text-gray-600 text-sm">{title}</p>
      <p className={`text-2xl font-bold mt-1 ${valueClassName}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function formatDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-CL');
}

function formatDateTime(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-CL');
}

